import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";

const SHORT_TICK_NAMES = new Set(["Year", "YTD", "Month", "Week"]);

function barEntry(name, period, rawValue, capAt) {
  const value = Number(rawValue) || 0;
  const isCapped = capAt != null && Math.abs(value) > capAt;
  const displayValue =
    isCapped && capAt != null ? (value > 0 ? capAt : -capAt) : value;
  return { name, value, displayValue, period, isCapped };
}

export default function PerformanceRadarChart(props) {
  // Memoize data preparation to avoid recalculation
  const chartData = useMemo(() => {
    const performanceData = props.performanceData;

    if (!performanceData) {
      return [
        barEntry("5Y (Ann.)", "5Y (Annualized)", 0, 200),
        barEntry("5Y (Act.)", "5Y (Actual return)", 0, 1500),
        barEntry("10Y (Ann.)", "10Y (Annualized)", 0, 200),
        barEntry("10Y (Act.)", "10Y (Actual return)", 0, 2000),
        barEntry("Year", "1 Year", 0, null),
        barEntry("YTD", "Year to Date", 0, null),
        barEntry("Month", "1 Month", 0, null),
        barEntry("Week", "1 Week", 0, null),
      ];
    }

    const perfYear = Number(performanceData.perfYear) || 0;
    const perfYTD = Number(performanceData.perfYTD) || 0;
    const perfMonth = Number(performanceData.perfMonth) || 0;
    const perfWeek = Number(performanceData.perfWeek) || 0;

    return [
      barEntry(
        "5Y (Ann.)",
        "5Y (Annualized)",
        performanceData.perf5Year,
        200
      ),
      barEntry(
        "5Y (Act.)",
        "5Y (Actual return)",
        performanceData.perf5YearTotal,
        1500
      ),
      barEntry(
        "10Y (Ann.)",
        "10Y (Annualized)",
        performanceData.perf10Year,
        200
      ),
      barEntry(
        "10Y (Act.)",
        "10Y (Actual return)",
        performanceData.perf10YearTotal,
        2000
      ),
      barEntry("Year", "1 Year", perfYear, null),
      barEntry("YTD", "Year to Date", perfYTD, null),
      barEntry("Month", "1 Month", perfMonth, null),
      barEntry("Week", "1 Week", perfWeek, null),
    ];
  }, [props.performanceData]);

  // Improved color logic with more granular thresholds
  const getBarColor = (value, isCapped) => {
    // If capped, use distinct colors to indicate off-scale
    if (isCapped) {
      return value > 0 ? "#2E8B57" : "#8B0000"; // Dark green/red for capped
    }

    // More granular color scale for better visual feedback
    if (value > 20) return "#52D017"; // Strong green for >20%
    if (value > 10) return "#7CFC00"; // Light green for 10-20%
    if (value > 5) return "#90EE90"; // Very light green for 5-10%
    if (value > 0) return "#98FB98"; // Pale green for 0-5%
    if (value === 0) return "#FFD700"; // Yellow for neutral
    if (value > -5) return "#FFB347"; // Light orange for 0 to -5%
    if (value > -10) return "#FFA500"; // Orange for -5 to -10%
    if (value > -20) return "#FF6347"; // Tomato for -10 to -20%
    return "#FF0000"; // Red for < -20%
  };

  // Calculate dynamic scale with memoization
  const { scaleMin, scaleMax } = useMemo(() => {
    const recentValues = chartData
      .filter((d) => SHORT_TICK_NAMES.has(d.name))
      .map((d) => d.displayValue);
    
    const recentMax = Math.max(...recentValues.map((v) => Math.abs(v)), 5);
    const recentMin = Math.min(...recentValues, -5);
    
    // Use scale optimized for recent performance with padding
    const baseMax = Math.max(recentMax * 1.5, 50);
    const baseMin = Math.min(recentMin * 1.5, -25);
    
    // Ensure all display values fit
    const allDisplayValues = chartData.map((d) => d.displayValue);
    const finalMax = Math.max(baseMax, ...allDisplayValues.map((v) => Math.abs(v)));
    const finalMin = Math.min(baseMin, ...allDisplayValues);

    return { scaleMin: finalMin, scaleMax: finalMax };
  }, [chartData]);

  // Custom tooltip with better formatting
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const value = data.value;
      const isCapped = data.isCapped;
      
      return (
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.97)",
            border: "1px solid #ccc",
            borderRadius: "6px",
            padding: "10px 14px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <div style={{ fontWeight: "700", color: "#333", marginBottom: "4px", fontSize: "13px" }}>
            {data.period}
          </div>
          <div style={{ color: value >= 0 ? "#52D017" : "#FF0000", fontWeight: "600", fontSize: "14px" }}>
            {value > 0 ? "+" : ""}{value.toFixed(2)}%
          </div>
          {isCapped && (
            <div style={{ fontSize: "11px", color: "#666", marginTop: "4px", fontStyle: "italic" }}>
              Capped at ±200% for display
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 5, bottom: 10 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            horizontal={true} 
            vertical={false} 
            stroke="rgba(0,0,0,0.08)" 
          />
          <XAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 9, fill: "#444", fontWeight: "600" }}
            axisLine={{ stroke: "#ddd", strokeWidth: 1.5 }}
            tickLine={{ stroke: "#ddd" }}
          />
          <YAxis
            type="number"
            domain={[scaleMin, scaleMax]}
            tick={{ fontSize: 10, fill: "#666", fontWeight: "500" }}
            tickFormatter={(value) => `${value > 0 ? "+" : ""}${value.toFixed(0)}%`}
            axisLine={{ stroke: "#ddd", strokeWidth: 1.5 }}
            tickLine={{ stroke: "#ddd" }}
            width={50}
          />
          {/* Add zero reference line for better readability */}
          <ReferenceLine 
            y={0} 
            stroke="#888" 
            strokeWidth={1.5} 
            strokeDasharray="5 5" 
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
          <Bar 
            dataKey="displayValue" 
            radius={[6, 6, 0, 0]}
            maxBarSize={56}
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={getBarColor(entry.value, entry.isCapped)}
                style={{ 
                  filter: 'brightness(1)',
                  transition: 'filter 0.2s ease'
                }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}